import React, { useState, useEffect } from 'react';
import { X, Search, Users, Briefcase, User } from 'lucide-react';

const CandidateSubmissionForm = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    candidate_id: '',
    job_id: '',
    job_title: '',
    client_name: '',
    recruiter_id: 'current_user',
    recruiter_name: 'Current User',
    client_contact_name: '',
    client_contact_email: '',
    hiring_manager: '',
    submission_notes: '',
    internal_comments: '',
    match_score: 0.8,
    priority_level: 'normal'
  });

  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [jobSearch, setJobSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchCandidates();
      fetchJobs();
    }
  }, [isOpen]);

  const fetchCandidates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/candidates`);
      if (response.ok) {
        const data = await response.json();
        setCandidates(data || []);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data || []);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation
      if (!formData.candidate_id || !formData.job_id) {
        alert('Please select both candidate and job');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Submission created:', result);
        
        // Reset form
        setFormData({
          candidate_id: '',
          job_id: '',
          job_title: '',
          client_name: '',
          recruiter_id: 'current_user',
          recruiter_name: 'Current User',
          client_contact_name: '',
          client_contact_email: '',
          hiring_manager: '',
          submission_notes: '',
          internal_comments: '',
          match_score: 0.8,
          priority_level: 'normal'
        });
        
        onSuccess && onSuccess(result);
        onClose();
      } else {
        const errorData = await response.json();
        alert(`Error creating submission: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating submission:', error);
      alert('Error creating submission. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCandidateSelect = (candidate) => {
    setFormData({
      ...formData,
      candidate_id: candidate.id,
    });
    setCandidateSearch(`${candidate.name} - ${candidate.email}`);
  };

  const handleJobSelect = (job) => {
    setFormData({
      ...formData,
      job_id: job.id,
      job_title: job.title,
      client_name: job.client_name || '',
      hiring_manager: job.hiring_manager || '',
    });
    setJobSearch(`${job.title} - ${job.department}`);
  };

  const filteredCandidates = candidates.filter(candidate =>
    candidate.name?.toLowerCase().includes(candidateSearch.toLowerCase()) ||
    candidate.email?.toLowerCase().includes(candidateSearch.toLowerCase())
  );

  const filteredJobs = jobs.filter(job =>
    job.title?.toLowerCase().includes(jobSearch.toLowerCase()) ||
    job.department?.toLowerCase().includes(jobSearch.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Create New Candidate Submission</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Candidate Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-2" />
                Select Candidate *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={candidateSearch}
                  onChange={(e) => setCandidateSearch(e.target.value)}
                  placeholder="Search candidates by name or email..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {candidateSearch && !formData.candidate_id && (
                  <div className="absolute top-full mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
                    {filteredCandidates.map((candidate) => (
                      <button
                        key={candidate.id}
                        type="button"
                        onClick={() => handleCandidateSelect(candidate)}
                        className="block w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium">{candidate.name}</div>
                        <div className="text-sm text-gray-600">{candidate.email}</div>
                        <div className="text-xs text-gray-500">{candidate.current_title} at {candidate.current_company}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Job Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Briefcase className="w-4 h-4 inline mr-2" />
                Select Job *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={jobSearch}
                  onChange={(e) => setJobSearch(e.target.value)}
                  placeholder="Search jobs by title or department..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {jobSearch && !formData.job_id && (
                  <div className="absolute top-full mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
                    {filteredJobs.map((job) => (
                      <button
                        key={job.id}
                        type="button"
                        onClick={() => handleJobSelect(job)}
                        className="block w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium">{job.title}</div>
                        <div className="text-sm text-gray-600">{job.department} • {job.location}</div>
                        <div className="text-xs text-gray-500">{job.employment_type} • {job.experience_level}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Client Information */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                placeholder="Enter client company name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Hiring Manager */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hiring Manager</label>
              <input
                type="text"
                value={formData.hiring_manager}
                onChange={(e) => setFormData({ ...formData, hiring_manager: e.target.value })}
                placeholder="Enter hiring manager name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Client Contact Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Client Contact Name</label>
              <input
                type="text"
                value={formData.client_contact_name}
                onChange={(e) => setFormData({ ...formData, client_contact_name: e.target.value })}
                placeholder="Enter client contact person"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Client Contact Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Client Contact Email</label>
              <input
                type="email"
                value={formData.client_contact_email}
                onChange={(e) => setFormData({ ...formData, client_contact_email: e.target.value })}
                placeholder="Enter client contact email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Priority Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority Level</label>
              <select
                value={formData.priority_level}
                onChange={(e) => setFormData({ ...formData, priority_level: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {/* Match Score */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Match Score</label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={formData.match_score}
                onChange={(e) => setFormData({ ...formData, match_score: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Score between 0.0 and 1.0</p>
            </div>
          </div>

          {/* Submission Notes */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Submission Notes</label>
            <textarea
              value={formData.submission_notes}
              onChange={(e) => setFormData({ ...formData, submission_notes: e.target.value })}
              placeholder="Add any notes about this submission..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Internal Comments */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Internal Comments</label>
            <textarea
              value={formData.internal_comments}
              onChange={(e) => setFormData({ ...formData, internal_comments: e.target.value })}
              placeholder="Add internal comments (not visible to client)..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.candidate_id || !formData.job_id}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Creating...' : 'Create Submission'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CandidateSubmissionForm;